import { useEffect, useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Search, User, Heart, ShoppingBag, Menu, X } from "lucide-react";
import { useStore } from "../context/StoreContext";
import { useAuth } from "../context/AuthContext";
import { useSiteContent } from "../context/SiteContentContext";
import CartDrawer from "./CartDrawer";
import SearchDialog from "./SearchDialog";

const NAV = [
  { label: "Shop", to: "/shop" },
  { label: "New Arrivals", to: "/shop?is_new=true" },
  { label: "Collections", to: "/collections" },
  { label: "Occasion", to: "/shop?occasion=Wedding" },
  { label: "Best Sellers", to: "/shop?is_bestseller=true" },
  { label: "Journal", to: "/journal", section: "section_journal" },
  { label: "About", to: "/about" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [cartOpen, setCartOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const { cartCount, wishlist } = useStore();
  const { user } = useAuth();
  const site = useSiteContent();
  const navigate = useNavigate();
  const location = useLocation();

  // Filter out any NAV entry whose section flag is turned off in admin content.
  const NAV_VISIBLE = NAV.filter((n) => !n.section || site.text(n.section, "on") !== "off");

  const isHome = location.pathname === "/";

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const solid = scrolled || !isHome;
  const textColor = solid ? "text-ink" : "text-ivory";

  return (
    <>
      <motion.header
        initial={{ y: -40, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.8, delay: 2.2, ease: [0.22, 1, 0.36, 1] }}
        className={`fixed top-0 left-0 right-0 z-50 transition-colors duration-500 ${
          solid ? "backdrop-blur-xl bg-ivory/80 border-b border-line" : "bg-transparent"
        }`}
        data-testid="navbar"
      >
        <div className="mx-auto flex h-16 md:h-20 max-w-[1600px] items-center justify-between px-5 md:px-10">
          {/* left nav */}
          <nav className={`hidden lg:flex items-center gap-7 text-[0.72rem] font-medium uppercase tracking-[0.18em] ${textColor}`}>
            {NAV_VISIBLE.slice(0, 4).map((n) => (
              <Link key={n.label} to={n.to} className="link-underline" data-testid={`nav-${n.label.toLowerCase().replace(/\s/g, "-")}`}>
                {n.label}
              </Link>
            ))}
          </nav>

          <button
            className={`lg:hidden ${textColor}`}
            onClick={() => setMobileOpen(true)}
            data-testid="mobile-menu-btn"
            aria-label="Menu"
          >
            <Menu size={22} />
          </button>

          {/* logo */}
          <Link
            to="/"
            className="absolute left-1/2 -translate-x-1/2 flex items-center"
            data-testid="logo"
          >
            <img
              src="/ambrai-logo.png"
              alt="Amraiya"
              className={`h-16 md:h-20 w-auto transition-[filter] duration-500 ${solid ? "" : "invert brightness-0 [filter:invert(1)_brightness(2)]"}`}
            />
          </Link>

          {/* right icons */}
          <div className={`flex items-center gap-4 md:gap-6 ${textColor}`}>
            <div className="hidden lg:flex items-center gap-7 text-[0.72rem] font-medium uppercase tracking-[0.18em] mr-2">
              {NAV_VISIBLE.slice(4).map((n) => (
                <Link key={n.label} to={n.to} className="link-underline" data-testid={`nav-${n.label.toLowerCase().replace(/\s/g, "-")}`}>
                  {n.label}
                </Link>
              ))}
            </div>
            <button onClick={() => setSearchOpen(true)} aria-label="Search" data-testid="search-btn" className="hover:text-gold transition-colors">
              <Search size={19} />
            </button>
            <button onClick={() => navigate(user ? "/account" : "/auth")} aria-label="Account" data-testid="account-btn" className="hover:text-gold transition-colors">
              <User size={19} />
            </button>
            <button onClick={() => navigate("/wishlist")} aria-label="Wishlist" data-testid="wishlist-btn" className="relative hover:text-gold transition-colors">
              <Heart size={19} />
              {wishlist.length > 0 && (
                <span className="absolute -right-2 -top-2 grid h-4 w-4 place-items-center rounded-full bg-gold text-[9px] text-ink">
                  {wishlist.length}
                </span>
              )}
            </button>
            <button onClick={() => setCartOpen(true)} aria-label="Cart" data-testid="cart-btn" className="relative hover:text-gold transition-colors">
              <ShoppingBag size={19} />
              {cartCount > 0 && (
                <span className="absolute -right-2 -top-2 grid h-4 w-4 place-items-center rounded-full bg-gold text-[9px] text-ink" data-testid="cart-count">
                  {cartCount}
                </span>
              )}
            </button>
          </div>
        </div>
      </motion.header>

      {/* mobile menu */}
      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            className="fixed inset-0 z-[60] bg-ink text-ivory"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            data-testid="mobile-menu"
          >
            <div className="flex items-center justify-between px-5 h-16 border-b border-white/10">
              <img src="/ambrai-logo.png" alt="Amraiya" className="h-20 w-auto" style={{ filter: "invert(1) brightness(2)" }} />
              <button onClick={() => setMobileOpen(false)} data-testid="mobile-close"><X /></button>
            </div>
            <nav className="flex flex-col px-6 pt-8 gap-1">
              {NAV_VISIBLE.map((n, i) => (
                <motion.div
                  key={n.label}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.05 * i }}
                >
                  <Link
                    to={n.to}
                    onClick={() => setMobileOpen(false)}
                    className="block font-serif text-4xl py-2 border-b border-white/10"
                  >
                    {n.label}
                  </Link>
                </motion.div>
              ))}
              <Link to="/contact" onClick={() => setMobileOpen(false)} className="mt-6 overline text-gold">Contact</Link>
            </nav>
          </motion.div>
        )}
      </AnimatePresence>

      <CartDrawer open={cartOpen} onOpenChange={setCartOpen} />
      <SearchDialog open={searchOpen} onOpenChange={setSearchOpen} />
    </>
  );
}
